import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, X, Paperclip, HelpCircle, Clock, CheckCircle2,
  MessageCircleQuestion, ChevronRight, User, FilterX,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { getQuestionsByAccount, subscribeToQuestions, createQuestion } from '../services/firebase/questions';
import { getAllInternalUsers } from '../services/internalUserService';
import { filterPortalAssignable } from '../utils/portalAssignable';
import { getInternalUsers } from '../services/firebase/users';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { formatTimeAgo } from '../utils/timeAgo';
import PortalBackground from '../components/PortalBackground';

const STATUS_META = {
  Open: {
    label: 'Open',
    icon: HelpCircle,
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    countText: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    ring: 'ring-2 ring-amber-400 dark:ring-amber-500',
  },
  'In Progress': {
    label: 'In Progress',
    icon: Clock,
    tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    countText: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    ring: 'ring-2 ring-blue-400 dark:ring-blue-500',
  },
  Resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    countText: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    ring: 'ring-2 ring-emerald-400 dark:ring-emerald-500',
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.Open;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const MyQuestionsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const { isMobile } = useResponsive();

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Load questions on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      const accountId = companyId || currentUser.uid;
      if (!accountId) return;

      try {
        setLoading(true);
        const questionsData = await getQuestionsByAccount(accountId);
        setQuestions(questionsData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, companyId]);

  // Set up real-time listener for questions
  useEffect(() => {
    if (!currentUser) return;
    const accountId = companyId || currentUser.uid;
    if (!accountId) return;

    const unsubscribe = subscribeToQuestions((questionsData) => {
      // Questions for current company (shared across team members)
      setQuestions(questionsData);
    }, accountId);

    return () => unsubscribe();
  }, [currentUser, companyId]);

  // Load internal users when modal opens
  useEffect(() => {
    if (showModal && internalUsers.length === 0) {
      const loadUsers = async () => {
        try {
          setModalLoading(true);

          // Try the internal users collection first, then fall back to users with isInternal flag
          let users = [];
          try {
            users = await getAllInternalUsers();
          } catch (error) {
            try {
              users = await getInternalUsers();
            } catch (fallbackError) {
              console.error('Failed to load internal users:', fallbackError);
              users = [];
            }
          }

          setInternalUsers(users || []);
          setModalLoading(false);
        } catch (error) {
          console.error('Error loading users:', error);
          setInternalUsers([]);
          setModalLoading(false);
        }
      };
      loadUsers();
    }
  }, [showModal, internalUsers.length]);

  // Handle file upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size <= 10 * 1024 * 1024) { // 10MB limit
        setFile(selectedFile);
        setFileError('');
      } else {
        setFileError('File must be less than 10MB');
        setFile(null);
      }
    }
  };

  // Remove file
  const removeFile = () => {
    setFile(null);
    setFileError('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!questionText.trim()) {
      alert('Please enter your question.');
      return;
    }

    if (!assigneeId) {
      alert('Please select someone to ask.');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedUser = internalUsers.find(user => user.id === assigneeId);

      const questionData = {
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: assigneeId === 'anyone' ? 'anyone' : assigneeId,
        assigneeName: assigneeId === 'anyone' ? 'Anyone' : (selectedUser?.name || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim()),
        questionText: questionText.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User'
      };

      await createQuestion(questionData);

      // Reset form and close modal
      setQuestionText('');
      setAssigneeId('');
      setFile(null);
      setFileError('');
      setShowModal(false);

    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Failed to submit question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setShowModal(false);
    setQuestionText('');
    setAssigneeId('');
    setFile(null);
    setFileError('');
    setIsSubmitting(false);
  };

  // Calculate quick stats
  const quickStats = {
    Open: questions.filter(question => question.status === 'Open').length,
    'In Progress': questions.filter(question => question.status === 'In Progress').length,
    Resolved: questions.filter(question => question.status === 'Resolved').length,
  };

  // Get unique values for filters
  const assignedToOptions = [...new Set(questions.map(question => question.assigneeName).filter(Boolean))];

  const hasActiveFilters = Boolean(search || assignedFilter || statusFilter);

  const clearFilters = () => {
    setSearch('');
    setAssignedFilter('');
    setStatusFilter('');
  };

  // Filter + sort questions (newest first)
  const visibleQuestions = useMemo(() => {
    return questions
      .filter(question => {
        const searchMatch = !search ||
          (question.questionText?.toLowerCase() || '').includes(search.toLowerCase()) ||
          (question.assigneeName?.toLowerCase() || '').includes(search.toLowerCase());

        const assignedMatch = !assignedFilter || question.assigneeName === assignedFilter;
        const statusMatch = !statusFilter || question.status === statusFilter;

        return searchMatch && assignedMatch && statusMatch;
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [questions, search, assignedFilter, statusFilter]);

  return (
    <div className={`relative container mx-auto ${isMobile ? 'px-3 py-4' : 'px-4 py-6'} min-h-screen`}>
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
          <BackButton onClick={() => navigate('/portal')} />
        </div>
        <div className={`text-center ${isMobile ? 'px-14' : 'px-44'} py-1`}>
          <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>My Questions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ask our team anything — we'll get back to you here.
          </p>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            {!isMobile && <span>Ask a Question</span>}
          </button>
        </div>
      </div>

      {/* Status stat cards — click to filter */}
      <div className={`grid grid-cols-3 ${isMobile ? 'gap-2 mb-4' : 'gap-4 mb-6'}`}>
        {Object.entries(STATUS_META).map(([status, meta]) => {
          const Icon = meta.icon;
          const count = quickStats[status];
          const active = statusFilter === status;
          return (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(active ? '' : status)}
              title={active ? 'Clear status filter' : `Show ${meta.label.toLowerCase()} questions`}
              className={`group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${
                isMobile ? 'p-3' : 'p-4'
              } ${active ? meta.ring : ''}`}
            >
              <div className={`flex items-center ${isMobile ? 'flex-col gap-1.5 text-center' : 'gap-3'}`}>
                <div className={`${isMobile ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                  <Icon className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold leading-none ${
                    count === 0 ? 'text-gray-300 dark:text-gray-600' : meta.countText
                  }`}>
                    {loading ? '…' : count}
                  </p>
                  <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{meta.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar: search + filters */}
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-3'} mb-4`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search your questions..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {assignedToOptions.length > 0 && (
          <select
            className={`${isMobile ? 'w-full' : 'w-48'} px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
          >
            <option value="">All Assignees</option>
            {assignedToOptions.map((assignee, idx) => (
              <option key={idx} value={assignee}>{assignee}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            <FilterX className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Questions list */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
              </div>
            </div>
          ))}
        </div>
      ) : visibleQuestions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
          <MessageCircleQuestion className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          {questions.length === 0 ? (
            <>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No questions yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                Stuck on something? Ask our team and track the conversation right here.
              </p>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ask Your First Question
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No questions match your filters</p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Clear filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {visibleQuestions.map(question => {
            const meta = STATUS_META[question.status] || STATUS_META.Open;
            const Icon = meta.icon;
            return (
              <button
                key={question.id}
                type="button"
                onClick={() => navigate(`/portal/question/${question.id}`)}
                className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex items-center gap-3"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta.tile}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                    {question.questionText}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <User className="w-3 h-3 shrink-0" />
                      <span className="truncate">{question.assigneeName || 'Unassigned'}</span>
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="whitespace-nowrap">{formatTimeAgo(question.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isMobile && <StatusBadge status={question.status} />}
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* New Question Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
              <button
                type="button"
                onClick={closeModal}
                className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                  <MessageCircleQuestion className="w-5 h-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ask a Question</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Our team typically responds within one business day.</p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5">
              {modalLoading ? (
                <div className="flex items-center justify-center py-8">
                  <BBALoader size={32} />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Question Text */}
                  <div>
                    <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Your Question <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="questionText"
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                      rows="5"
                      placeholder="What would you like to know? The more detail, the better we can help."
                      autoFocus
                      required
                    />
                  </div>

                  {/* Assignee Selection */}
                  <div>
                    <label htmlFor="assigneeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Who should answer? <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="assigneeId"
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    >
                      <option value="" disabled>Select someone to ask...</option>
                      <option value="anyone">Anyone on the team</option>
                      {filterPortalAssignable(internalUsers).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      Not sure who to ask? Choose "Anyone on the team."
                    </p>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Attachment <span className="text-gray-400 font-normal">(optional, max 10MB)</span>
                    </label>
                    {file ? (
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:border-red-300 dark:hover:border-red-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <Paperclip className="w-4 h-4" />
                        Click to attach a file
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                        />
                      </label>
                    )}
                    {fileError && (
                      <p className="text-red-500 text-xs mt-1">{fileError}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !questionText.trim() || !assigneeId}
                      className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Question'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MyQuestionsPage;
